import { useState, useEffect, useCallback } from 'react';
import notificationService, { PushSubscriptionData } from '../services/notificationService';

export interface PushNotificationState {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
}

export const usePushNotifications = () => {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    isLoading: false,
    error: null
  });

  const [subscriptionData, setSubscriptionData] = useState<PushSubscriptionData | null>(null);

  // Initialize the notification service
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        const isSupported = await notificationService.initialize();
        const permission = await notificationService.getPermissionStatus();
        const subscription = await notificationService.getCurrentSubscription();
        
        setState(prev => ({
          ...prev,
          isSupported,
          permission,
          isSubscribed: !!subscription
        }));

        if (subscription) {
          const data = await notificationService.getSubscriptionData();
          setSubscriptionData(data);
        }
      } catch (error) {
        console.error('Failed to initialize notifications:', error);
        setState(prev => ({
          ...prev,
          error: 'Failed to initialize notifications'
        }));
      }
    };

    initializeNotifications();
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const permission = await notificationService.requestPermission();
      setState(prev => ({ ...prev, permission, isLoading: false }));
      return permission;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to request permission';
      setState(prev => ({ ...prev, error: errorMessage, isLoading: false }));
      throw error;
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const subscription = await notificationService.subscribeToPushNotifications();
      const data = await notificationService.getSubscriptionData();
      
      setState(prev => ({
        ...prev,
        isSubscribed: !!subscription,
        isLoading: false
      }));
      
      setSubscriptionData(data);
      return subscription;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to subscribe';
      setState(prev => ({ ...prev, error: errorMessage, isLoading: false }));
      throw error;
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const success = await notificationService.unsubscribeFromPushNotifications();
      
      if (success) {
        setState(prev => ({
          ...prev,
          isSubscribed: false,
          isLoading: false
        }));
        setSubscriptionData(null);
      }
      
      return success;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unsubscribe';
      setState(prev => ({ ...prev, error: errorMessage, isLoading: false }));
      throw error;
    }
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    setState(prev => ({ ...prev, error: null }));
    
    try {
      await notificationService.sendTestNotification();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send test notification';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    subscriptionData,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification,
    clearError
  };
}; 